import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Chuyển động tròn đều (Lớp 10 — Bài 18/19): v = ω·r (tiếp tuyến), gia tốc hướng tâm
// a = ω²·r = v²/r (hướng vào tâm); lực hướng tâm F = m·a. Chu kì T = 2π/ω.
const CircularMotionSimulation: React.FC<Props> = ({
  title = 'Chuyển động tròn đều',
  description = 'Vận tốc v luôn tiếp tuyến; gia tốc hướng tâm a hướng vào tâm với a = ω²r = v²/r.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const lastRef = useRef<number | null>(null)
  const thetaRef = useRef(0)
  const [running, setRunning] = useState(true)
  const [omega, setOmega] = useState(1.2)
  const [r, setR] = useState(1)
  const firstRef = useRef(false)
  const W = 460, H = 320
  const cx = W / 2, cy = H / 2 - 10

  const arrow = (ctx: CanvasRenderingContext2D, x: number, y: number, dx: number, dy: number, color: string) => {
    const ex = x + dx, ey = y + dy, a = Math.atan2(dy, dx)
    ctx.strokeStyle = color; ctx.fillStyle = color; ctx.lineWidth = 3
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(ex, ey); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(ex - 10 * Math.cos(a - 0.4), ey - 10 * Math.sin(a - 0.4)); ctx.lineTo(ex - 10 * Math.cos(a + 0.4), ey - 10 * Math.sin(a + 0.4)); ctx.closePath(); ctx.fill()
  }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const Rpix = 60 + r * 60
    const th = thetaRef.current
    const ox = cx + Rpix * Math.cos(th), oy = cy + Rpix * Math.sin(th)
    // quỹ đạo
    ctx.strokeStyle = '#cbd5e1'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4])
    ctx.beginPath(); ctx.arc(cx, cy, Rpix, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([])
    // bán kính
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(ox, oy); ctx.stroke()
    // tâm
    ctx.fillStyle = '#334155'; ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fill()
    // vector vận tốc (tiếp tuyến) — xanh lá
    const vMag = omega * r * 40
    arrow(ctx, ox, oy, -Math.sin(th) * vMag, Math.cos(th) * vMag, '#10b981')
    // gia tốc hướng tâm (vào tâm) — đỏ
    const aMag = Math.min(70, omega * omega * r * 22)
    arrow(ctx, ox, oy, -Math.cos(th) * aMag, -Math.sin(th) * aMag, '#ef4444')
    // vật
    const g = ctx.createRadialGradient(ox - 4, oy - 4, 2, ox, oy, 12)
    g.addColorStop(0, '#bfdbfe'); g.addColorStop(1, '#1d4ed8'); ctx.fillStyle = g
    ctx.beginPath(); ctx.arc(ox, oy, 12, 0, Math.PI * 2); ctx.fill()
    // chú thích
    ctx.fillStyle = '#10b981'; ctx.font = 'bold 12px sans-serif'; ctx.fillText('v', ox - Math.sin(th) * vMag, oy + Math.cos(th) * vMag - 6)
    ctx.fillStyle = '#ef4444'; ctx.fillText('a', ox - Math.cos(th) * aMag - 10, oy - Math.sin(th) * aMag)
  }, [omega, r])

  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.05, (ts - lastRef.current) / 1000); lastRef.current = ts
    thetaRef.current += omega * dt; draw(); raf.current = requestAnimationFrame(tick)
  }, [omega, draw])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])
  useEffect(() => { draw() }, [draw])

  const T = (2 * Math.PI / omega).toFixed(2), v = (omega * r).toFixed(2), a = (omega * omega * r).toFixed(2)
  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="flex flex-col md:flex-row gap-4">
        <canvas ref={cv} width={W} height={H} className="rounded-lg border border-gray-100 bg-slate-50" />
        <div className="flex-1">
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="p-2 rounded-lg bg-violet-50"><div className="font-black text-violet-700">{T}s</div><div className="text-[11px] text-gray-500">Chu kì T</div></div>
            <div className="p-2 rounded-lg bg-emerald-50"><div className="font-black text-emerald-700">{v}</div><div className="text-[11px] text-gray-500">Tốc độ v=ωr</div></div>
            <div className="p-2 rounded-lg bg-rose-50"><div className="font-black text-rose-700">{a}</div><div className="text-[11px] text-gray-500">a=ω²r</div></div>
          </div>
          <label className="flex items-center gap-2 text-sm mb-2"><span className="w-28 text-gray-600">Tốc độ góc ω</span><input type="range" min={0.4} max={3} step={0.1} value={omega} onChange={(e) => setOmega(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{omega.toFixed(1)}</span></label>
          <label className="flex items-center gap-2 text-sm mb-3"><span className="w-28 text-gray-600">Bán kính r</span><input type="range" min={0.4} max={2} step={0.1} value={r} onChange={(e) => setR(Number(e.target.value))} className="flex-1" /><span className="w-10 text-right font-semibold">{r.toFixed(1)}</span></label>
          <button onClick={() => setRunning((x) => !x)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">
            {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        </div>
      </div>
    </div>
  )
}
export default CircularMotionSimulation
